import { Link } from 'react-router-dom'
import {ArrowUpRight, ShieldCheck, TrendingUp} from 'lucide-react'
import type { ProjectProfile } from '@/lib/types'
import { CHAIN_LABELS, toProjectSlug } from '@/lib/types'
import AuditBadge from '@/components/AuditBadge'
import { useShareAudit } from '@/components/AuditShareCard'

function XLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}

function shorten(addr: string) {
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr
}

function ScoreChip({ score, verdict }: { score: number | null; verdict: string | null }) {
  if (score === null) return null
  const color =
    score >= 80 ? '#22c55e' :
    score >= 60 ? '#1ad6c4' :
    score >= 40 ? '#f59e0b' : '#ef4444'
  const bg =
    score >= 80 ? 'rgba(34,197,94,0.12)' :
    score >= 60 ? 'rgba(26,214,196,0.12)' :
    score >= 40 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)'

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <span
        className="text-sm font-semibold leading-none px-2 py-1 rounded-md"
        style={{ background: bg, color }}
      >
        {score}
      </span>
      <TrendingUp className="w-3 h-3" style={{ color }} />
    </div>
  )
}

function ProjectCard({ project }: { project: ProjectProfile }) {
  const completed = project.audit?.status === 'completed'
  const score = project.audit?.score ?? null
  const verdict = project.audit?.verdict ?? null
  const { share: handleShare, loading: shareLoading, CardPortal } = useShareAudit(project)

  const verdictColor =
    verdict === 'passed' ? '#22c55e' :
    verdict === 'failed' ? '#ef4444' : '#1ad6c4'

  return (
    <div
      className="group rounded-xl overflow-hidden flex flex-col transition-all duration-200 hover:-translate-y-1"
      style={{ background: '#070a0c', border: '1px solid rgba(26,214,196,0.14)', boxShadow: '0 4px 24px rgba(0,0,0,0.5)' }}
    >
      {/* Banner */}
      <div className="h-24 relative" style={{ background: 'linear-gradient(120deg,#05080a,#0a1518)' }}>
        {project.bannerUrl ? (
          <img src={project.bannerUrl} alt={`${project.name} banner`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg,rgba(26,214,196,0.12),rgba(26,214,196,0.05))' }} />
        )}

        {/* Score chip — top right on banner */}
        {score !== null && (
          <div className="absolute top-2.5 right-3">
            <ScoreChip score={score} verdict={verdict} />
          </div>
        )}

        {/* Logo */}
        <div className="absolute -bottom-6 left-5">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-black font-semibold text-lg overflow-hidden"
            style={{ background: 'linear-gradient(135deg,#1ad6c4,#3ce8d6)', border: '3px solid #070a0c' }}
          >
            {project.logoUrl ? (
              <img src={project.logoUrl} alt={project.name} className="w-full h-full object-cover rounded-md" />
            ) : (
              project.symbol.slice(0, 1)
            )}
          </div>
        </div>
      </div>

      <div className="pt-8 px-5 pb-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-white">{project.name}</h3>
            <p className="text-xs mt-0.5" style={{ color: '#6a7682' }}>
              ${project.symbol} · {CHAIN_LABELS[project.chain]}
            </p>
          </div>
          <AuditBadge audit={project.audit} />
        </div>

        <p className="text-sm mt-3 line-clamp-2 flex-1" style={{ color: '#b4c0cc' }}>
          {project.description}
        </p>

        {/* Verdict strip */}
        {completed && verdict && (
          <div
            className="mt-3 flex items-center gap-1.5 text-xs font-medium"
            style={{ color: verdictColor }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Audit {verdict === 'passed' ? 'Passed' : 'Failed'}
          </div>
        )}

        <p className="text-xs mt-2 font-mono" style={{ color: '#4a5662' }}>
          {shorten(project.contractAddress)}
        </p>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {completed ? (
            <Link
              to={`/projects/${toProjectSlug(project.name)}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-black transition-all hover:brightness-110"
              style={{ background: '#1ad6c4', boxShadow: '0 0 14px rgba(26,214,196,0.3)' }}
            >
              <ShieldCheck className="w-4 h-4" />
              View Audit
            </Link>
          ) : (
            <Link
              to={`/projects/${toProjectSlug(project.name)}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors hover:bg-white/5"
              style={{ color: '#b4c0cc', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              View Profile
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          )}
          {completed && (
            <button
              onClick={(e) => { e.preventDefault(); handleShare() }}
              disabled={shareLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: '#b4c0cc', border: '1px solid rgba(255,255,255,0.10)' }}
              title="Share this audit on X"
            >
              <XLogo className="w-3.5 h-3.5" />
              Share
            </button>
          )}
        </div>
        {/* Off-screen share card */}
        <CardPortal />
      </div>
    </div>
  )
}

export default ProjectCard
